/**
 * Envio de erros ao GlitchTip self-hosted (erros.vixio.com.br), que fala o protocolo do Sentry. Sem SDK de terceiros:
 * um evento JSON por erro no endpoint /api/<projeto>/store/, com limite por janela e sem dado pessoal (LGPD): URLs
 * perdem query e fragmento, e-mails, CPFs e tokens viram "[removido]", nada de usuário, cookie ou corpo de requisição.
 */

export interface Dsn {
  /** Endpoint de envio com a chave pública na query (aceito como requisição simples, sem preflight). */
  storeUrl: string;
  /** Origem do servidor (para a CSP connect-src). */
  origin: string;
}

/** DSN do Sentry/GlitchTip: https://<chave>@host[:porta]/<projeto>. Vazio, "off" ou inválido: null (envio desligado). */
export function parseDsn(dsn: string | undefined | null): Dsn | null {
  const raw = (dsn ?? "").trim();
  if (!raw || raw === "off") return null;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  const key = decodeURIComponent(u.username);
  const project = u.pathname.replace(/\/+$/, "").split("/").pop() ?? "";
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  if (!key || !/^\d+$/.test(project)) return null;
  const prefix = u.pathname.replace(/\/+$/, "").slice(0, -project.length).replace(/\/+$/, "");
  const origin = `${u.protocol}//${u.host}`;
  const storeUrl = `${origin}${prefix}/api/${project}/store/?sentry_version=7&sentry_client=vixio-errors%2F1&sentry_key=${encodeURIComponent(key)}`;
  return { storeUrl, origin };
}

const SCRUBBERS: [RegExp, string][] = [
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[e-mail removido]"],
  [/\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g, "[cpf removido]"],
  [/\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g, "[cnpj removido]"],
  [/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, "[jwt removido]"],
  [/\b(Bearer|apikey|token|password|senha|secret)([=:\s"']+)[^\s"'&,;]+/gi, "$1$2[removido]"],
  [/(https?:\/\/[^\s?#"']+)[?#][^\s"']*/g, "$1"],
];

/** Tira dado pessoal e segredo de um texto (mensagem, pilha, URL). */
export function scrub(text: string): string {
  let out = text;
  for (const [re, rep] of SCRUBBERS) out = out.replace(re, rep);
  return out.length > 4000 ? `${out.slice(0, 4000)}…` : out;
}

/** URL sem query nem fragmento (onde podem estar tokens de convite, códigos e e-mails). */
export function stripUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    return scrub(url.split(/[?#]/)[0] ?? "");
  }
}

export interface Frame {
  filename?: string;
  function?: string;
  lineno?: number;
  colno?: number;
  in_app?: boolean;
}

/** Quadros da pilha (V8 e Firefox/Safari), do mais antigo para o mais recente, como o Sentry espera. */
export function parseStack(stack: string | undefined): Frame[] {
  if (!stack) return [];
  const frames: Frame[] = [];
  for (const line of stack.split("\n").slice(0, 60)) {
    const v8 = /^\s*at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?\s*$/.exec(line);
    const gecko = v8 ? null : /^\s*(.*?)@(.+?):(\d+):(\d+)\s*$/.exec(line);
    const m = v8 ?? gecko;
    if (!m) continue;
    const filename = stripUrl(m[2] ?? "");
    frames.push({
      function: m[1] || "?",
      filename,
      lineno: Number(m[3]),
      colno: Number(m[4]),
      in_app: !/node_modules|node:internal|^node:/.test(filename),
    });
  }
  return frames.reverse();
}

export interface ReporterOptions {
  dsn: string | undefined | null;
  /** "javascript" no navegador, "node" no worker. */
  platform: "javascript" | "node";
  /** Ex.: "production", "staging". */
  environment?: string;
  /** Versão (commit) do build, quando houver. */
  release?: string;
  /** Marcas fixas (app, serviço). */
  tags?: Record<string, string>;
  /** Máximo de eventos por janela de 1 minuto (padrão 10); o resto é descartado. */
  maxPerMinute?: number;
  /** Envio (padrão: fetch). Substituível em teste. */
  transport?: (url: string, body: string) => Promise<unknown>;
  now?: () => number;
}

export interface CaptureContext {
  /** Onde aconteceu (rota do painel, laço do worker...). */
  where?: string;
  /** URL da página (vai sem query). */
  url?: string;
  tags?: Record<string, string>;
  level?: "fatal" | "error" | "warning";
}

export interface Reporter {
  enabled: boolean;
  /** Origem do GlitchTip, para a CSP. */
  origin: string | null;
  capture(error: unknown, ctx?: CaptureContext): string | null;
  /** Espera os envios em andamento (fim de processo). */
  flush(): Promise<void>;
}

function eventId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID().replace(/-/g, "");
  let s = "";
  for (let i = 0; i < 32; i++) s += Math.floor(Math.random() * 16).toString(16);
  return s;
}

function asError(error: unknown): { type: string; message: string; stack?: string } {
  if (error instanceof Error) return { type: error.name || "Error", message: error.message, stack: error.stack };
  if (typeof error === "string") return { type: "Error", message: error };
  try {
    return { type: "Error", message: JSON.stringify(error) ?? String(error) };
  } catch {
    return { type: "Error", message: String(error) };
  }
}

export function createReporter(opts: ReporterOptions): Reporter {
  const dsn = parseDsn(opts.dsn);
  const max = opts.maxPerMinute ?? 10;
  const now = opts.now ?? Date.now;
  const transport =
    opts.transport ??
    ((url: string, body: string) =>
      fetch(url, {
        method: "POST",
        // text/plain: requisição simples (sem preflight CORS), aceita pelo endpoint do Sentry/GlitchTip.
        headers: { "content-type": "text/plain;charset=UTF-8" },
        body,
        keepalive: body.length < 60_000,
      }));
  const pending = new Set<Promise<unknown>>();
  const seen = new Map<string, number>();
  let windowStart = 0;
  let sent = 0;

  function capture(error: unknown, ctx: CaptureContext = {}): string | null {
    if (!dsn) return null;
    const t = now();
    if (t - windowStart > 60_000) {
      windowStart = t;
      sent = 0;
      seen.clear();
    }
    const e = asError(error);
    const message = scrub(e.message);
    // O mesmo erro repetido na janela conta uma vez só.
    const fingerprint = `${e.type}:${message}:${ctx.where ?? ""}`;
    if (seen.has(fingerprint) || sent >= max) return null;
    seen.set(fingerprint, t);
    sent++;
    const id = eventId();
    const frames = parseStack(e.stack ? scrub(e.stack) : undefined);
    const event = {
      event_id: id,
      timestamp: new Date(t).toISOString(),
      platform: opts.platform,
      level: ctx.level ?? "error",
      environment: opts.environment || undefined,
      release: opts.release || undefined,
      transaction: ctx.where ? scrub(ctx.where) : undefined,
      tags: { ...opts.tags, ...ctx.tags },
      request: ctx.url ? { url: stripUrl(ctx.url) } : undefined,
      exception: {
        values: [{ type: e.type, value: message, stacktrace: frames.length ? { frames } : undefined }],
      },
    };
    const p = transport(dsn.storeUrl, JSON.stringify(event))
      .catch(() => undefined)
      .finally(() => pending.delete(p));
    pending.add(p);
    return id;
  }

  return {
    enabled: dsn !== null,
    origin: dsn?.origin ?? null,
    capture,
    async flush() {
      await Promise.all([...pending]);
    },
  };
}
